// Sơ đồ Hệ thống bản máy tính (đổi 31/10 — điện thoại / iPad vẫn là cây thụt lề NetworkTreeRow): ô gốc nối ra các
// khối nhóm Đại lý / CTV / Shop, bấm khối → cột đơn vị (8 ô + Xem thêm), bấm đơn vị → khối nhóm cấp dưới… Cột, vị trí,
// đường nối tính sẵn ở @shoptrack/common/network-drill; ở đây chỉ vẽ:
//  · khối nhóm 148×68 (số to + "N cần xử lý" / "N có đơn", vài lớp thẻ xếp chồng phía sau khi nhóm có nhiều đơn vị),
//    ô đơn vị 184×46 (thân ô = mở nhánh, nút ⓘ = ngăn chi tiết; Shop không có cấp dưới thì thân ô cũng mở ngăn),
//    ô gốc nền màu nhấn (bấm = ngăn tóm tắt);
//  · đường nối khuỷu bo góc bằng viền div; đường tới ô đang mở tô màu nhấn.
// Cả khối nằm giữa khung (đo bằng ResizeObserver) và trượt mượt mỗi khi đổi chỗ — ô cùng khoá giữ nguyên phần tử nên
// transition left/top chạy được. Khung cuộn cả hai chiều khi sơ đồ to hơn khung; sau mỗi bước cuộn cho cột vừa mở
// (hay ô vừa chọn) vào tầm nhìn, bên trái ngăn chi tiết. Ngăn (NetworkSidePanel) đè lên mép phải, KHÔNG chặn sơ đồ.

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { ChevronRight, Info, Search } from 'lucide-react'
import {
  networkDrillLayout,
  type NetworkDrillBox, type NetworkDrillLine,
} from '@shoptrack/common/network-drill'
import type { NetworkNode, NetworkUnitBase } from '@shoptrack/common/network-tree'
import { cn } from '@/lib/utils'
import { networkFocusScroll } from './network-view'
import { NETWORK_TYPE_META } from './NetworkTreeRow'
import type { NetworkDrillView } from './useNetworkTreeState'

/** Những gì một ô hiện — trang tự soạn theo dữ liệu của mình (NPP / Đại lý / CTV khác API). */
export interface NetworkCardView {
  /** Chữ tắt trong ô vuông (ĐL / CTV / S / NPP). */
  short: string
  /** Lớp màu loại (tone-orange…); ô gốc bỏ trống. */
  tone?: string
  name: string
  sub: string
  /** Chấm việc cần xử lý ở góc phải trên. */
  dot?: 'danger' | 'warn' | null
  /** Nhãn cho trình đọc màn hình (tên + dòng phụ + việc cần xử lý). */
  label: string
}

const DOT = { danger: 'bg-danger', warn: 'bg-warn' } as const
/** Nhịp trượt khi sơ đồ đổi chỗ (cùng nhịp với ngăn chi tiết). */
const MOVE = 'transition-[left,top,width,height] duration-300 ease-[cubic-bezier(.2,.8,.2,1)] motion-reduce:transition-none'
const vn = (n: number) => n.toLocaleString('vi-VN')

export function NetworkCanvas<U extends NetworkUnitBase & { orders30d?: number | null }>({
  drill,
  selected,
  root,
  describe,
  hasIssue,
  showOrders = true,
  emptyText,
  panelWidth,
  panel,
  className,
}: {
  drill: NetworkDrillView<U>
  /** Ô đang mở ngăn chi tiết (mã đơn vị, hoặc khoá ô gốc). */
  selected: string | null
  /** Ô gốc (NPP / chính mình). */
  root: NetworkCardView
  describe: (node: NetworkNode<U>) => NetworkCardView
  hasIssue: (unit: U) => boolean
  /** Có số đơn 30 ngày (nhân viên thiếu quyền xem đơn thì không) — khối nhóm ghi "N có đơn". */
  showOrders?: boolean
  /** Đang tìm / lọc mà không có kết quả. */
  emptyText: string
  /** Bề ngang ngăn chi tiết đang mở (0 = đóng) — sơ đồ dời sang trái, chừa chỗ cho ngăn. */
  panelWidth: number
  /** Ngăn chi tiết (NetworkSidePanel) — nằm trong khung sơ đồ, không cuộn theo sơ đồ. */
  panel?: ReactNode
  className?: string
}) {
  const hostRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState<{ w: number; h: number } | null>(null)
  // Đo khung trước lần vẽ đầu (sơ đồ nằm giữa ngay, không trượt từ góc vào); đổi cỡ cửa sổ thì canh lại
  useLayoutEffect(() => {
    const host = hostRef.current
    if (!host) return
    const measure = () => setSize((cur) => (cur && cur.w === host.clientWidth && cur.h === host.clientHeight ? cur : { w: host.clientWidth, h: host.clientHeight }))
    measure()
    if (typeof ResizeObserver !== 'function') return
    const ro = new ResizeObserver(measure)
    ro.observe(host)
    return () => ro.disconnect()
  }, [])

  const reserve = panelWidth ? panelWidth + 16 : 0
  const L = useMemo(
    () => (size ? networkDrillLayout(drill.columns, { width: size.w, height: size.h, reserveRight: reserve }) : null),
    [drill.columns, size, reserve],
  )

  // Sau mỗi bước (mở / đóng nhánh, vào / ra kết quả, chọn ô): cuộn cho cột vừa mở — hay ô vừa chọn khi ngăn mở — vào
  // tầm nhìn. "Xem thêm" không đổi khoá → không cuộn (người dùng đang ở đáy cột).
  const stepKey = drill.columns.map((c) => c.open?.key ?? '').join('|') + `#${selected ?? ''}`
  const latest = useRef({ L, selected, reserve })
  latest.current = { L, selected, reserve }
  useEffect(() => {
    const host = hostRef.current
    const { L: lay, selected: sel, reserve: rr } = latest.current
    if (!host || !lay || typeof host.scrollTo !== 'function') return
    const selBox = sel ? lay.boxes.find((b) => b.item.kind === 'unit' && b.item.node.unit.id === sel) : undefined
    const to = networkFocusScroll(
      { left: host.scrollLeft, top: host.scrollTop, width: host.clientWidth, height: host.clientHeight },
      selBox && rr ? selBox : lay.focus,
      rr,
    )
    if (to) host.scrollTo({ ...to, behavior: 'smooth' })
  }, [stepKey, !!L]) // eslint-disable-line react-hooks/exhaustive-deps

  const width = L ? L.width : 0
  const height = L ? L.height : 0

  return (
    <div className={cn('relative min-h-0 flex-1 bg-paper/50', className)}>
      <div ref={hostRef} className="absolute inset-0 overflow-auto overscroll-contain">
        <div
          className="relative min-h-full min-w-full"
          style={{
            width,
            height,
            backgroundImage: 'radial-gradient(rgb(var(--muted) / 0.16) 1px, transparent 1px)',
            backgroundSize: '18px 18px',
          }}
        >
          {L && (
            <>
              {L.lines.map((l) => (
                <Line key={l.key} line={l} />
              ))}
              {L.boxes.map((b) => (
                <Box
                  key={b.item.key}
                  box={b}
                  drill={drill}
                  selected={selected}
                  root={root}
                  describe={describe}
                  hasIssue={hasIssue}
                  showOrders={showOrders}
                  emptyText={emptyText}
                />
              ))}
            </>
          )}
        </div>
      </div>
      {panel}
    </div>
  )
}

/** Đoạn nối: gạch ngang (viền trên) hoặc khuỷu (viền trái + viền dưới / trên, bo góc phía ô cấp dưới). */
function Line({ line: l }: { line: NetworkDrillLine }) {
  const t = l.on ? 2 : 1.5
  const style: CSSProperties =
    l.shape === 'stem'
      ? { left: l.x, top: l.y - t / 2, width: l.w + t / 2, height: t, borderTopWidth: t }
      : {
          left: l.x - t / 2,
          top: l.y - t / 2,
          width: l.w + t / 2,
          height: l.h + t,
          borderLeftWidth: t,
          ...(l.shape === 'down' ? { borderBottomWidth: t, borderBottomLeftRadius: l.radius } : { borderTopWidth: t, borderTopLeftRadius: l.radius }),
        }
  return <div aria-hidden style={style} className={cn('pointer-events-none absolute border-solid', MOVE, l.on ? 'z-[1] border-accent' : 'border-muted/40')} />
}

function Box<U extends NetworkUnitBase & { orders30d?: number | null }>({
  box: b,
  drill,
  selected,
  root,
  describe,
  hasIssue,
  showOrders,
  emptyText,
}: {
  box: NetworkDrillBox<U>
  drill: NetworkDrillView<U>
  selected: string | null
  root: NetworkCardView
  describe: (node: NetworkNode<U>) => NetworkCardView
  hasIssue: (unit: U) => boolean
  showOrders: boolean
  emptyText: string
}) {
  const it = b.item
  const pos: CSSProperties = { left: b.x, top: b.y, width: b.w, height: b.h }
  // Ô mới hiện thì mờ dần vào; ô đã có thì trượt tới chỗ mới
  const wrap = cn('absolute z-[2] animate-in fade-in-0 duration-300', MOVE)
  const activate = () => drill.activate(b.col, it)

  if (it.kind === 'root') {
    const isSel = selected === it.key
    return (
      <div style={pos} data-kind={it.kind} className={wrap}>
        <button
          type="button"
          onClick={activate}
          aria-label={root.label}
          aria-pressed={isSel}
          className={cn(
            'motion-state flex size-full items-center gap-2.5 rounded-xl bg-accent px-3 text-left shadow-[0_8px_22px_rgb(var(--accent)/0.24)] hover:-translate-y-px',
            isSel && 'ring-4 ring-accent/20',
          )}
        >
          <span aria-hidden className="flex size-[34px] shrink-0 items-center justify-center rounded-[9px] bg-onAccent/20 text-[10px] font-bold text-onAccent">
            {root.short}
          </span>
          <span className="flex min-w-0 flex-col gap-px">
            <span className="truncate text-sm font-semibold text-onAccent">{root.name}</span>
            <span className="truncate text-[11.5px] text-onAccent/90">{root.sub}</span>
          </span>
        </button>
      </div>
    )
  }

  if (it.kind === 'group' || it.kind === 'results') {
    const n = it.units.length
    const isRes = it.kind === 'results'
    const meta = it.kind === 'group' ? NETWORK_TYPE_META[it.type] : null
    const label = meta ? meta.label : drill.resultsHead.label
    const issues = isRes ? 0 : it.units.filter((x) => hasIssue(x.unit)).length
    const active = isRes || !showOrders ? 0 : it.units.filter((x) => drill.ix.act(x) > 0).length
    const sub = isRes ? 'kết quả' : issues ? `${vn(issues)} cần xử lý` : showOrders ? (active ? `${vn(active)} có đơn` : 'chưa có đơn') : ''
    // Màu loại: lớp tone-… trên khung (đặt biến --tone-bg / --tone-fg cho cả khối); khối kết quả màu nhấn
    const border = meta ? 'border-toneFg/20' : 'border-accent/30'
    const fill = meta ? 'bg-toneBg/40' : 'bg-accent/5'
    const sheet = (cls: string) => (
      <span aria-hidden className={cn('absolute overflow-hidden rounded-[14px] border bg-raise', border, cls)}>
        <span className={cn('absolute inset-0', fill)} />
      </span>
    )
    return (
      <div style={pos} data-kind={it.kind} className={cn(wrap, meta?.tone)}>
        {/* Thẻ xếp chồng phía sau: nhóm có nhiều đơn vị trông "dày" hơn */}
        {n >= 3 && sheet('inset-x-3 -bottom-2.5 top-2.5')}
        {n >= 2 && sheet('inset-x-1.5 -bottom-[5px] top-[5px]')}
        <button
          type="button"
          onClick={activate}
          aria-expanded={isRes ? undefined : b.open}
          aria-label={isRes ? `${label}: ${vn(n)} kết quả, bấm để thoát` : `${label}: ${vn(n)}${sub ? `, ${sub}` : ''}`}
          className={cn(
            'motion-state relative flex size-full flex-col justify-between overflow-hidden rounded-[14px] bg-raise py-[9px] pl-3 pr-[11px] text-left shadow-sm hover:-translate-y-px hover:shadow-[0_6px_16px_rgb(var(--ink)/0.09)]',
            b.open ? 'border-[1.5px] border-accent ring-[3px] ring-accent/15' : cn('border', border),
          )}
        >
          <span aria-hidden className={cn('absolute inset-0', fill)} />
          <span className="relative flex min-w-0 items-center gap-[7px] text-[12.5px] font-semibold text-ink">
            <span aria-hidden className={cn('flex h-5 min-w-[22px] shrink-0 items-center justify-center rounded-md px-[3px] text-[9px] font-bold', meta ? 'bg-toneBg text-toneFg' : 'bg-accent/15 text-accent')}>
              {meta ? meta.short : drill.resultsHead.short || <Search className="size-3" strokeWidth={2.5} />}
            </span>
            <span className="truncate">{label}</span>
            <ChevronRight aria-hidden className={cn('ml-auto size-[15px] shrink-0 transition-transform duration-200', b.open ? 'rotate-180 text-accent' : 'text-muted')} strokeWidth={2.2} />
          </span>
          <span className="relative flex min-w-0 items-baseline gap-[7px]">
            <span className="shrink-0 text-2xl font-bold leading-none tracking-tight text-ink tabular-nums">{vn(n)}</span>
            {sub && <span className={cn('truncate text-[11px]', issues ? 'text-warn' : 'text-muted')}>{sub}</span>}
          </span>
        </button>
      </div>
    )
  }

  if (it.kind === 'unit') {
    const node = it.node
    const v = describe(node)
    const isSel = selected === node.unit.id
    const branch = !it.result && node.children.length > 0
    // Kết quả tìm: dòng phụ là đường đi (cấp trên → …), bấm là nhảy vào cây
    const sub = it.result ? resultPath(node, root.name) : v.sub
    return (
      <div style={pos} data-kind={it.kind} className={wrap}>
        <button
          type="button"
          onClick={activate}
          aria-expanded={branch ? b.open : undefined}
          aria-label={it.result ? `${v.label}, thuộc ${sub}` : v.label}
          title={node.unit.name}
          className={cn(
            'motion-state flex size-full items-center gap-2 rounded-[10px] bg-raise pl-[9px] pr-9 text-left shadow-sm hover:-translate-y-px hover:shadow-[0_6px_14px_rgb(var(--ink)/0.09)]',
            isSel
              ? 'border-[1.5px] border-accent shadow-[0_8px_20px_rgb(var(--ink)/0.1)] ring-[3px] ring-accent/20'
              : b.open
                ? 'border-[1.5px] border-accent bg-accent/5'
                : 'border border-rule hover:border-muted/45',
          )}
        >
          <span aria-hidden className={cn(v.tone, 'flex h-5 min-w-[22px] shrink-0 items-center justify-center rounded-md bg-toneBg px-[3px] text-[9px] font-bold text-toneFg')}>
            {v.short}
          </span>
          <span className="flex min-w-0 flex-col gap-px">
            <span className="truncate text-[12.5px] font-semibold leading-4 text-ink">{v.name}</span>
            <span className="truncate text-[11px] leading-[14px] text-muted">{sub}</span>
          </span>
        </button>
        {/* Nút nhìn thấy 26px, vùng bấm 44px — ::before nới ra mỗi bên 9px (giao kèo vùng chạm) */}
        <button
          type="button"
          onClick={() => drill.info(node)}
          aria-label={`Chi tiết ${node.unit.name}`}
          aria-pressed={isSel}
          title="Chi tiết"
          className={cn(
            'motion-state absolute right-[5px] top-1/2 flex size-[26px] -translate-y-1/2 items-center justify-center rounded-lg before:absolute before:-inset-[9px] hover:bg-paperSoft hover:text-accent',
            isSel ? 'text-accent' : 'text-muted',
          )}
        >
          <Info className="size-[15px]" aria-hidden />
        </button>
        {v.dot && <span aria-hidden className={cn('pointer-events-none absolute -right-[3px] -top-[3px] size-2.5 rounded-full ring-2 ring-raise', DOT[v.dot])} />}
      </div>
    )
  }

  if (it.kind === 'more') {
    return (
      <div style={pos} data-kind={it.kind} className={wrap}>
        <button
          type="button"
          onClick={activate}
          className="motion-state flex size-full items-center justify-center gap-[5px] rounded-[10px] border border-dashed border-muted/45 bg-raise/70 text-[12.5px] font-semibold text-accent hover:border-accent hover:bg-accent/5"
        >
          Xem thêm {vn(it.add)}
          <span className="font-normal text-muted">· còn {vn(it.left)}</span>
        </button>
      </div>
    )
  }

  return (
    <p style={{ ...pos, width: 260 }} data-kind="empty" className={cn('absolute m-0 text-[13px] text-muted', MOVE)}>
      {emptyText}
    </p>
  )
}

/** "Đại lý Minh Phát › CTV Lan" — chuỗi cấp trên của một kết quả tìm; trực thuộc gốc thì ghi tên gốc. */
function resultPath(node: NetworkNode<NetworkUnitBase>, rootName: string): string {
  const names: string[] = []
  for (let p = node.parent; p; p = p.parent) names.unshift(p.unit.name)
  return names.length ? names.join(' › ') : `Trực thuộc ${rootName}`
}
